// ---------------------------------------------------------------------------
// jsonFetch
//
// Thin, surface-agnostic JSON fetch helper for the rare cases where a request
// must be issued outside an Inertia visit (e.g. a fire-and-reload mutation that
// then triggers an Inertia partial reload to refresh server state). Centralizes
// CSRF + header assembly + error handling so callers do not hand-roll each one.
//
// Contract:
//   - Reads the CSRF token via getCsrfToken() and sends it as X-CSRF-Token.
//   - Sets Accept: application/json; sets Content-Type: application/json unless
//     the body is FormData (the browser must set the multipart boundary itself).
//   - NEVER throws: a non-2xx response resolves to { ok: false, status, body },
//     and a network/parse error resolves to { ok: false, status: 0, body: null }.
//   - The caller passes the URL; no endpoint URLs are baked in here.
//
// Surface-agnostic and dumb: carries no privileged data, no role/auth logic.
// Safe to import from any of the three bundles.
// ---------------------------------------------------------------------------

import { getCsrfToken } from './csrf'

export interface JsonFetchResult<T> {
  ok: boolean
  status: number
  body: T | null
}

export interface JsonFetchOptions {
  method?: string
  // A JSON-serializable payload, or FormData for multipart uploads.
  body?: unknown
  // Additional headers merged over the defaults.
  headers?: Record<string, string>
}

export async function jsonFetch<T>(
  url: string,
  options: JsonFetchOptions = {},
): Promise<JsonFetchResult<T>> {
  const { method = 'GET', body, headers: extraHeaders } = options

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...extraHeaders,
  }

  const csrfToken = getCsrfToken()
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }

  let requestBody: BodyInit | undefined
  if (body !== undefined && body !== null) {
    requestBody = isFormData ? (body as FormData) : JSON.stringify(body)
  }

  try {
    const res = await fetch(url, { method, headers, body: requestBody })

    let parsed: T | null = null
    try {
      parsed = (await res.json()) as T
    } catch {
      // Empty / non-JSON response body (e.g. 204 No Content) — leave body null.
      parsed = null
    }

    return { ok: res.ok, status: res.status, body: parsed }
  } catch {
    // Network error (offline, DNS, CORS) — surface as a non-ok result.
    return { ok: false, status: 0, body: null }
  }
}
